import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  LogOut, Users, CalendarDays, Plus, ChevronLeft, ChevronRight,
  AlertTriangle, Clock, PoundSterling, Search, X, Mail, Paperclip, FileText,
  ChevronDown, ChevronUp, Calendar as CalendarIcon
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ customers: 0, bookings: 0, expiredDocs: 0 });
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [showUpcoming, setShowUpcoming] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBlockedDates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/blocked-dates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBlockedDates(data);
      }
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  }, [API_URL]);

  // Simple date formatter for API calls
  const toDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchBookings = useCallback(async () => {
    try {
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      let url = `${API_URL}/api/bookings/calendar?start_date=${toDateString(startOfMonth)}&end_date=${toDateString(endOfMonth)}`;
      
      if (selectedCustomer && selectedCustomer !== 'all') {
        url += `&customer_id=${selectedCustomer}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  }, [API_URL, currentMonth, selectedCustomer]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [API_URL]);

  const fetchStats = useCallback(async () => {
    try {
      const [customersRes, bookingsRes] = await Promise.all([
        fetch(`${API_URL}/api/customers`, { credentials: 'include' }),
        fetch(`${API_URL}/api/bookings`, { credentials: 'include' })
      ]);

      if (customersRes.ok && bookingsRes.ok) {
        const customersData = await customersRes.json();
        const allBookings = await bookingsRes.json();
        
        let expiredDocs = 0;
        customersData.forEach(customer => {
          const docs = customer.documents || {};
          Object.values(docs).forEach(doc => {
            if (doc && doc.is_expired) expiredDocs++;
          });
        });

        setStats({
          customers: customersData.length,
          bookings: allBookings.length,
          expiredDocs
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [API_URL]);

  const fetchUpcomingBookings = useCallback(async () => {
    try {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setMonth(futureDate.getMonth() + 6); // Look 6 months ahead
      
      const url = `${API_URL}/api/bookings/calendar?start_date=${toDateString(today)}&end_date=${toDateString(futureDate)}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        const todayStr = toDateString(today);
        
        // Filter to bookings that start from today onwards and sort by collection date
        const upcoming = data
          .filter(b => b.collection_date >= todayStr)
          .sort((a, b) => {
            if (a.collection_date === b.collection_date) {
              return a.collection_time.localeCompare(b.collection_time);
            }
            return a.collection_date.localeCompare(b.collection_date);
          })
          .slice(0, 3); // Take only first 3
        
        setUpcomingBookings(upcoming);
      }
    } catch (error) {
      console.error('Error fetching upcoming bookings:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBookings(), fetchStats(), fetchCustomers(), fetchUpcomingBookings(), fetchBlockedDates()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBookings, fetchStats, fetchCustomers, fetchUpcomingBookings, fetchBlockedDates]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Helper to parse date string as local date (avoids timezone shifts)
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper to format date as YYYY-MM-DD in local time
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if a date is blocked
  const isDateBlocked = (date) => {
    const dateStr = formatLocalDate(date);
    return blockedDates.some(blocked => 
      dateStr >= blocked.start_date && dateStr <= blocked.end_date
    );
  };

  // Get blocked date info for a specific date
  const getBlockedDateInfo = (date) => {
    const dateStr = formatLocalDate(date);
    return blockedDates.find(blocked => 
      dateStr >= blocked.start_date && dateStr <= blocked.end_date
    );
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return bookings.filter(booking => {
      return dateStr >= booking.collection_date && dateStr <= booking.return_date;
    });
  };

  // Get bookings for selected date
  const selectedDateBookings = getBookingsForDate(selectedDate);
  const selectedDateBlocked = getBlockedDateInfo(selectedDate);

  // Count bookings per date to detect conflicts
  const bookingCountPerDate = bookings.reduce((acc, booking) => {
    const start = parseLocalDate(booking.collection_date);
    const end = parseLocalDate(booking.return_date);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatLocalDate(d);
      acc[dateStr] = (acc[dateStr] || 0) + 1;
    }
    return acc;
  }, {});

  // Track enquiry-only dates (where ALL bookings for that date are enquiry only)
  const enquiryOnlyDates = bookings.reduce((acc, booking) => {
    const start = parseLocalDate(booking.collection_date);
    const end = parseLocalDate(booking.return_date);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatLocalDate(d);
      if (acc[dateStr] === undefined) {
        acc[dateStr] = booking.is_enquiry_only;
      } else {
        // If any booking on this date is NOT enquiry only, mark as false
        acc[dateStr] = acc[dateStr] && booking.is_enquiry_only;
      }
    }
    return acc;
  }, {});

  // Track dates with late collection (at or after 12:00) - top-left white triangle (morning available)
  const lateCollectionDates = new Set(
    bookings
      .filter(b => b.collection_time >= "12:00")
      .map(b => b.collection_date)
  );

  // Track dates with early return (at or before 13:00) - bottom-right white triangle (afternoon available)
  const earlyReturnDates = new Set(
    bookings
      .filter(b => b.return_time <= "13:00")
      .map(b => b.return_date)
  );

  // Dates that have bookings (for calendar highlighting)
  const bookedDates = new Set(Object.keys(bookingCountPerDate));
  const conflictDates = new Set(
    Object.entries(bookingCountPerDate)
      .filter(([, count]) => count > 1)
      .map(([date]) => date)
  );
  const enquiryDates = new Set(
    Object.entries(enquiryOnlyDates)
      .filter(([date, isEnquiry]) => isEnquiry && !conflictDates.has(date))
      .map(([date]) => date)
  );

  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#f5f3ed]">Equi-Go Admin</h1>
            <p className="text-[#f5f3ed]/70 text-sm">{user?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#2d5f3f]/50 rounded-xl p-6 border border-[#2d5f3f]">
            <div className="flex items-center gap-4">
              <div className="bg-[#c9a55a]/20 p-3 rounded-lg">
                <Users className="w-6 h-6 text-[#c9a55a]" />
              </div>
              <div>
                <p className="text-[#f5f3ed]/70 text-sm">Total Customers</p>
                <p className="text-3xl font-bold text-[#f5f3ed]">{stats.customers}</p>
              </div>
            </div>
          </div>

          <div className="bg-[#2d5f3f]/50 rounded-xl p-6 border border-[#2d5f3f]">
            <div className="flex items-center gap-4">
              <div className="bg-[#c9a55a]/20 p-3 rounded-lg">
                <CalendarDays className="w-6 h-6 text-[#c9a55a]" />
              </div>
              <div>
                <p className="text-[#f5f3ed]/70 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold text-[#f5f3ed]">{stats.bookings}</p>
              </div>
            </div>
          </div>

          <div className={`rounded-xl p-6 border ${stats.expiredDocs > 0 ? 'bg-red-500/20 border-red-500/50' : 'bg-[#2d5f3f]/50 border-[#2d5f3f]'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stats.expiredDocs > 0 ? 'bg-red-500/20' : 'bg-[#c9a55a]/20'}`}>
                <AlertTriangle className={`w-6 h-6 ${stats.expiredDocs > 0 ? 'text-red-400' : 'text-[#c9a55a]'}`} />
              </div>
              <div>
                <p className={`text-sm ${stats.expiredDocs > 0 ? 'text-red-200' : 'text-[#f5f3ed]/70'}`}>Expired Documents</p>
                <p className={`text-3xl font-bold ${stats.expiredDocs > 0 ? 'text-red-400' : 'text-[#f5f3ed]'}`}>{stats.expiredDocs}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button
            onClick={() => navigate('/admin/customers')}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
            data-testid="manage-customers-btn"
          >
            <Users className="w-5 h-5 mr-2" />
            Manage Customers
          </Button>
          <Button
            onClick={() => navigate('/admin/bookings')}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
            data-testid="manage-bookings-btn"
          >
            <CalendarDays className="w-5 h-5 mr-2" />
            Manage Bookings
          </Button>
          <Button
            onClick={() => navigate('/admin/customers/new')}
            variant="outline"
            className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
            data-testid="new-customer-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Customer
          </Button>
          <Button
            onClick={() => navigate('/admin/bookings/new')}
            variant="outline"
            className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
            data-testid="new-booking-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Booking
          </Button>
          
          {/* Email System Buttons */}
          <div className="w-px h-8 bg-[#2d5f3f] mx-2"></div>
          <Button
            onClick={() => navigate('/admin/send-email')}
            variant="outline"
            className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
            data-testid="send-emails-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            Send Emails
          </Button>
          <Button
            onClick={() => navigate('/admin/email-templates')}
            variant="outline"
            className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
            data-testid="email-templates-btn"
          >
            <FileText className="w-5 h-5 mr-2" />
            Templates
          </Button>
          <Button
            onClick={() => navigate('/admin/attachments')}
            variant="outline"
            className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
            data-testid="attachments-btn"
          >
            <Paperclip className="w-5 h-5 mr-2" />
            Attachments
          </Button>
          
          {/* Unavailable Dates Button */}
          <div className="w-px h-8 bg-[#2d5f3f] mx-2"></div>
          <Button
            onClick={() => navigate('/admin/blocked-dates')}
            variant="outline"
            className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
            data-testid="blocked-dates-btn"
          >
            <CalendarDays className="w-5 h-5 mr-2" />
            Unavailable Dates
          </Button>
        </div>

        {/* Customer Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-[#f5f3ed]/70">
              <Search className="w-5 h-5" />
              <span>Filter by Customer:</span>
            </div>
            <Select
              value={selectedCustomer}
              onValueChange={setSelectedCustomer}
            >
              <SelectTrigger className="w-[280px] bg-[#2d5f3f]/30 border-[#2d5f3f] text-[#f5f3ed]" data-testid="customer-filter">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1f1a] border-[#2d5f3f]">
                <SelectItem value="all" className="text-[#f5f3ed] hover:bg-[#2d5f3f]/50 focus:bg-[#2d5f3f]/50">
                  All Customers
                </SelectItem>
                {customers.map(customer => (
                  <SelectItem 
                    key={customer.customer_id} 
                    value={customer.customer_id}
                    className="text-[#f5f3ed] hover:bg-[#2d5f3f]/50 focus:bg-[#2d5f3f]/50"
                  >
                    {customer.display_id ? `${customer.display_id} - ` : ''}{customer.first_name} {customer.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCustomer !== 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCustomer('all')}
                className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filter
              </Button>
            )}
          </div>
        </div>

        {/* Calendar Section - Made Larger */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          {/* Calendar - Takes more space */}
          <div className="xl:col-span-3 bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f5f3ed]">Booking Calendar</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleMonthChange(-1)}
                  className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="text-[#f5f3ed] font-medium min-w-[150px] text-center">
                  {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleMonthChange(1)}
                  className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#c9a55a]"></div>
                <span className="text-[#f5f3ed]/70">Confirmed Booking</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#9ca3af]"></div>
                <span className="text-[#f5f3ed]/70">Enquiry Only</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-[#f5f3ed]/70">Conflict</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span className="text-[#f5f3ed]/70">Unavailable</span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{ background: 'linear-gradient(135deg, white 50%, #c9a55a 50%)' }}
                ></div>
                <span className="text-[#f5f3ed]/70">Late Collection (at/after 12:00)</span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{ background: 'linear-gradient(135deg, #c9a55a 50%, white 50%)' }}
                ></div>
                <span className="text-[#f5f3ed]/70">Early Return (at/before 13:00)</span>
              </div>
            </div>
            
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              weekStartsOn={1}
              className="rounded-lg bg-white p-4 text-[#1a1f1a] w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_.rdp-cell]:text-center [&_.rdp-head_cell]:text-center [&_.rdp-day]:w-12 [&_.rdp-day]:h-12 [&_.rdp-day]:text-lg"
              modifiers={{
                booked: (date) => {
                  const dateStr = formatLocalDate(date);
                  return bookedDates.has(dateStr) && !conflictDates.has(dateStr) && !enquiryDates.has(dateStr) && !isDateBlocked(date);
                },
                enquiry: (date) => {
                  const dateStr = formatLocalDate(date);
                  return enquiryDates.has(dateStr) && !isDateBlocked(date);
                },
                conflict: (date) => conflictDates.has(formatLocalDate(date)),
                blocked: (date) => isDateBlocked(date),
                lateCollection: (date) => lateCollectionDates.has(formatLocalDate(date)),
                earlyReturn: (date) => earlyReturnDates.has(formatLocalDate(date))
              }}
              modifiersStyles={{
                booked: { 
                  backgroundColor: '#c9a55a',
                  color: '#1a1f1a',
                  fontWeight: 'bold'
                },
                enquiry: {
                  backgroundColor: '#9ca3af',
                  color: '#1a1f1a',
                  fontWeight: 'bold'
                },
                conflict: {
                  backgroundColor: '#ef4444',
                  color: 'white',
                  fontWeight: 'bold'
                },
                blocked: {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontWeight: 'bold'
                }
              }}
              components={{
                DayContent: ({ date, activeModifiers }) => {
                  const dateStr = formatLocalDate(date);
                  const hasLateCollection = lateCollectionDates.has(dateStr);
                  const hasEarlyReturn = earlyReturnDates.has(dateStr);
                  const isBooked = bookedDates.has(dateStr) && !conflictDates.has(dateStr) && !enquiryDates.has(dateStr) && !isDateBlocked(date);
                  const isEnquiry = enquiryDates.has(dateStr) && !isDateBlocked(date);
                  const isConflict = conflictDates.has(dateStr);
                  const isBlocked = isDateBlocked(date);
                  
                  // Determine the booking color for this date
                  let bookingColor = null;
                  if (isConflict) bookingColor = '#ef4444';
                  else if (isBooked) bookingColor = '#c9a55a';
                  else if (isEnquiry) bookingColor = '#9ca3af';
                  else if (isBlocked) bookingColor = '#3b82f6';
                  
                  const hasBooking = !!bookingColor;
                  
                  // Build gradient style for partial days
                  let gradientStyle = {};
                  if (hasBooking && (hasLateCollection || hasEarlyReturn)) {
                    if (hasLateCollection && hasEarlyReturn) {
                      // Both: white corners on both sides (rare case - single day late pickup & early return)
                      gradientStyle = {
                        background: `linear-gradient(135deg, white 25%, ${bookingColor} 25%, ${bookingColor} 75%, white 75%)`,
                        color: '#1a1f1a'
                      };
                    } else if (hasLateCollection) {
                      // Late collection: white top-left, color bottom-right
                      gradientStyle = {
                        background: `linear-gradient(135deg, white 50%, ${bookingColor} 50%)`,
                        color: '#1a1f1a'
                      };
                    } else if (hasEarlyReturn) {
                      // Early return: color top-left, white bottom-right
                      gradientStyle = {
                        background: `linear-gradient(135deg, ${bookingColor} 50%, white 50%)`,
                        color: isConflict ? 'white' : '#1a1f1a'
                      };
                    }
                  }
                  
                  return (
                    <div 
                      className="relative w-full h-full flex items-center justify-center rounded-md"
                      style={gradientStyle}
                      title={
                        hasLateCollection && hasEarlyReturn ? "Late collection & early return" :
                        hasLateCollection ? "Late collection (at/after 12:00)" :
                        hasEarlyReturn ? "Early return (at/before 13:00)" : undefined
                      }
                    >
                      <span className="font-bold relative z-10">{date.getDate()}</span>
                    </div>
                  );
                }
              }}
            />
          </div>

          {/* Bookings Sidebar */}
          <div className="xl:col-span-2 space-y-6">
            {/* Selected Date Bookings */}
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h3 className="text-lg font-semibold text-[#f5f3ed] mb-4">
                {selectedDate.toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </h3>
              
              {selectedDateBlocked && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3 flex items-center gap-2 mb-4">
                  <CalendarDays className="w-5 h-5 text-blue-400" />
                  <div>
                    <span className="text-blue-200 text-sm font-medium">Unavailable for Hire</span>
                    {selectedDateBlocked.notes && (
                      <p className="text-blue-200/70 text-xs">{selectedDateBlocked.notes}</p>
                    )}
                  </div>
                </div>
              )}
              
              {selectedDateBookings.length === 0 && !selectedDateBlocked ? (
                <p className="text-[#f5f3ed]/50 text-sm">No bookings on this date</p>
              ) : selectedDateBookings.length === 0 ? null : (
                <div className="space-y-4 max-h-[300px] overflow-y-auto">
                  {selectedDateBookings.length > 1 && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="text-red-200 text-sm">Multiple bookings on this date!</span>
                    </div>
                  )}
                  {selectedDateBookings.map(booking => (
                    <div 
                      key={booking.booking_id}
                      className={`rounded-lg p-4 cursor-pointer transition-colors ${
                        booking.is_enquiry_only 
                          ? 'bg-[#9ca3af]/20 hover:bg-[#9ca3af]/30 border border-[#9ca3af]/30' 
                          : 'bg-[#1a1f1a]/50 hover:bg-[#1a1f1a]/70'
                      }`}
                      onClick={() => navigate(`/admin/bookings/${booking.booking_id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[#f5f3ed] font-medium text-lg">{booking.customer_name}</p>
                          {booking.display_id && (
                            <p className="text-[#c9a55a] text-sm">{booking.display_id}</p>
                          )}
                        </div>
                        {booking.is_enquiry_only && (
                          <span className="bg-[#9ca3af]/20 text-[#9ca3af] px-2 py-1 rounded text-xs font-medium">
                            Enquiry Only
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-[#f5f3ed]/70 text-sm">
                        <Clock className="w-4 h-4" />
                        <span>
                          {booking.collection_date === formatLocalDate(selectedDate) 
                            ? `Collection: ${booking.collection_time}`
                            : booking.return_date === formatLocalDate(selectedDate)
                            ? `Return: ${booking.return_time}`
                            : 'In hire'}
                        </span>
                      </div>
                      {booking.balance_due > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-[#c9a55a] text-sm font-medium">
                          <PoundSterling className="w-4 h-4" />
                          <span>Balance due: £{booking.balance_due.toFixed(2)}</span>
                        </div>
                      )}
                      {booking.balance_due <= 0 && (
                        <div className="flex items-center gap-2 mt-2 text-green-400 text-sm">
                          <PoundSterling className="w-4 h-4" />
                          <span>Paid in full</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Bookings */}
            <div className="bg-[#2d5f3f]/30 rounded-xl border border-[#2d5f3f] overflow-hidden">
              <button
                onClick={() => setShowUpcoming(!showUpcoming)}
                className="w-full p-4 flex items-center justify-between hover:bg-[#2d5f3f]/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#c9a55a]" />
                  <h3 className="text-lg font-semibold text-[#f5f3ed]">Upcoming Bookings</h3>
                  {upcomingBookings.length > 0 && (
                    <span className="bg-[#c9a55a] text-[#1a1f1a] text-xs font-bold px-2 py-0.5 rounded-full">
                      {upcomingBookings.length}
                    </span>
                  )}
                </div>
                {showUpcoming ? (
                  <ChevronUp className="w-5 h-5 text-[#f5f3ed]/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#f5f3ed]/50" />
                )}
              </button>
              
              {showUpcoming && (
                <div className="p-4 pt-0 border-t border-[#2d5f3f]/50">
                  {upcomingBookings.length === 0 ? (
                    <p className="text-[#f5f3ed]/50 text-sm py-2">No upcoming bookings</p>
                  ) : (
                    <div className="space-y-3 mt-4">
                      {upcomingBookings.map(booking => {
                        const collectionDate = new Date(booking.collection_date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const daysUntil = Math.ceil((collectionDate - today) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <div 
                            key={booking.booking_id}
                            className={`rounded-lg p-3 cursor-pointer transition-colors ${
                              booking.is_enquiry_only 
                                ? 'bg-[#9ca3af]/20 hover:bg-[#9ca3af]/30 border border-[#9ca3af]/30' 
                                : 'bg-[#1a1f1a]/50 hover:bg-[#1a1f1a]/70'
                            }`}
                            onClick={() => navigate(`/admin/bookings/${booking.booking_id}`)}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-[#f5f3ed] font-medium">{booking.customer_name}</p>
                                {booking.display_id && (
                                  <p className="text-[#c9a55a] text-xs">{booking.display_id}</p>
                                )}
                              </div>
                              <div className="text-right">
                                {daysUntil === 0 ? (
                                  <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                    Today
                                  </span>
                                ) : daysUntil === 1 ? (
                                  <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded text-xs font-medium">
                                    Tomorrow
                                  </span>
                                ) : (
                                  <span className="text-[#f5f3ed]/50 text-xs">
                                    In {daysUntil} days
                                  </span>
                                )}
                                {booking.is_enquiry_only && (
                                  <span className="block mt-1 text-[#9ca3af] text-xs">
                                    Enquiry
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-[#f5f3ed]/60 text-xs">
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="w-3 h-3" />
                                <span>
                                  {collectionDate.toLocaleDateString('en-GB', { 
                                    day: 'numeric', 
                                    month: 'short'
                                  })}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>{booking.collection_time}</span>
                              </div>
                              {booking.balance_due > 0 && (
                                <div className="flex items-center gap-1 text-[#c9a55a]">
                                  <PoundSterling className="w-3 h-3" />
                                  <span>£{booking.balance_due.toFixed(2)} due</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
